import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, shareReplay, switchMap, tap, timer } from 'rxjs';
import { whoIcdCredentialsToken } from './app.config';
import { LoadingUIEnabled } from './loading.interceptor';

type WhoTokenResponse = {
  access_token: string;
  expires_in?: number; // seconds (WHO returns ~3600)
  token_type?: string;
  scope?: string;
};

@Injectable({
  providedIn: 'root'
})
export class WhoicdtokenService {
  private http = inject(HttpClient);
  private creds = inject(whoIcdCredentialsToken);

  private readonly tokenUrl = 'https://icdaccessmanagement.who.int/connect/token';
  private readonly refreshSkewMs = 60_000; // refresh 1 min early

  private tokenState$ = new BehaviorSubject<{ token: string; expiresAtMs: number } | null>(null);

  // single in-flight fetch to avoid multiple callers racing
  private inflight$?: Observable<string>;

  get token$(): Observable<string> {
// $#$    // ...do not use /* alert() */ in services; it will spam and block UI...
    return this.tokenState$.pipe(
      switchMap((s) => {
        if (s?.token && Date.now() < (s.expiresAtMs - this.refreshSkewMs)) return of(s.token);
        return this.fetchToken();
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private fetchToken(): Observable<string> {
    if (this.inflight$) return this.inflight$;

    const body = new URLSearchParams();
    body.set('grant_type', 'client_credentials');
    body.set('client_id', this.creds.clientId);
    body.set('client_secret', this.creds.clientSecret);
    body.set('scope', 'icdapi_access');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    this.inflight$ = this.http.post<WhoTokenResponse>(this.tokenUrl, body.toString(), { headers, context: new HttpContext().set(LoadingUIEnabled, false) }).pipe(
      map((r) => {
        const ttlMs = (r.expires_in ?? 3600) * 1000;
        const expiresAtMs = Date.now() + ttlMs;
        this.tokenState$.next({ token: r.access_token, expiresAtMs });

        // schedule refresh
        const refreshInMs = Math.max(1_000, ttlMs - this.refreshSkewMs);
        timer(refreshInMs).subscribe(() => {
          // fire-and-forget refresh; next consumers will use updated tokenState$
          this.inflight$ = undefined;
          this.fetchToken().subscribe({ error: () => { /* noop */ } });
        });

        return r.access_token;
      }),
      tap(() => (this.inflight$ = undefined)),
      catchError((err) => {
        this.inflight$ = undefined;
        console.error('WHO ICD token fetch failed', err);
        return of('');
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    return this.inflight$;
  }
}
